import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';

export interface NavItem {
  to: string;
  label: string;
  icon: string;
  /**
   * Defaults to true when `to` has at most one path segment beyond root.
   * Set explicitly to false for list routes that have nested detail pages
   * so the sidebar item stays highlighted while viewing a detail page.
   */
  exact?: boolean;
}

/** A non-navigable section label rendered above the NavItems that follow it — one per module workspace. */
export interface NavHeader {
  header: string;
}

type NavEntry = NavItem | NavHeader;

function isHeader(entry: NavEntry): entry is NavHeader {
  return 'header' in entry;
}

/**
 * Sidebar shell — same skeleton as every sibling portal's Shell.tsx
 * (servicedesk-portal, homeaffairs-portal). `items` may interleave plain
 * NavItems with NavHeader section labels — this portal covers three
 * distinct module workspaces (Fisheries / Agriculture & Bio-security / Blue
 * Economy Projects), so grouped headers keep the sidebar legible.
 */
export function Shell({ title, items }: { title: string; items: NavEntry[] }) {
  const { persona, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <div className="flex min-h-screen">
      <aside className="fixed inset-y-0 left-0 flex w-64 flex-col overflow-y-auto bg-ink-950">
        <div className="flex items-center gap-2.5 px-5 py-5">
          <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand-600 text-sm font-black text-white">SC</div>
          <div>
            <div className="text-sm font-bold leading-tight text-white">One Gov Blue Economy</div>
            <div className="text-[11px] text-slate-400">{title}</div>
          </div>
        </div>
        <nav className="flex-1 space-y-0.5 px-3 py-2">
          {items.map((item, i) =>
            isHeader(item) ? (
              <div
                key={`h-${i}`}
                className="px-3 pb-1 pt-3 text-[10px] font-bold uppercase tracking-wider text-slate-500 first:pt-1"
              >
                {item.header}
              </div>
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact ?? item.to.split('/').length <= 2}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition ${
                    isActive ? 'bg-brand-600 text-white' : 'text-slate-300 hover:bg-ink-800 hover:text-white'
                  }`
                }
              >
                <span className="w-5 text-center">{item.icon}</span>
                {item.label}
              </NavLink>
            ),
          )}
        </nav>
        <div className="border-t border-ink-800 px-5 py-4">
          <div className="text-sm font-semibold text-white">{persona?.name}</div>
          <div className="text-[11px] leading-snug text-slate-400">{persona?.title}</div>
          <div className="text-[11px] leading-snug text-slate-500">{persona?.org}</div>
          <button
            className="mt-3 w-full rounded-lg border border-ink-700 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-ink-800"
            onClick={() => {
              logout();
              navigate('/');
            }}
          >
            Switch user
          </button>
        </div>
      </aside>
      <main className="ml-64 flex-1 px-8 py-7">
        <Outlet />
      </main>
    </div>
  );
}
